/**
 * Reports Page - Compliance Report Generation
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Download, ShieldCheck, Lock, Shield, FileText, AlertTriangle,
  Bug, Eye, Globe, Loader2, Package 
} from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ReportsPage() {
  const [generating, setGenerating] = useState(null);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      
      const response = await fetch(`${API_URL}/api/reports/`, {
        credentials: 'include',
        
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleGenerateReport = async (type) => {
    setGenerating(type);
    try {
      
      const response = await fetch(`${API_URL}/api/reports/generate?report_type=${type}`, {
        method: 'POST',
        credentials: 'include',
        
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Report generated! Starting download...');
        
        if (data.report_id) {
          // Download the report using fetch with credentials
          try {
            const downloadResponse = await fetch(`${API_URL}/api/reports/${data.report_id}/download`, {
              credentials: 'include'
            });
            if (downloadResponse.ok) {
              const blob = await downloadResponse.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `threebie_report_${data.report_id}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }
          } catch (downloadError) {
            console.error('Download error:', downloadError);
          }
        }
        
        fetchReports();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(null);
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const response = await fetch(`${API_URL}/api/reports/${reportId}/download`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        toast.error('Download failed');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `threebie_report_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    }
  };

  const reportTypes = [
    { id: 'compliance_summary', title: 'SOC 2 Compliance Summary', description: 'Overall posture, category breakdown, and key findings.', icon: ShieldCheck, color: '#FF00BE' },
    { id: 'security_overview', title: 'Security Architecture Overview', description: 'Infrastructure, application, and data protection controls.', icon: Lock, color: '#059669' },
    { id: 'control_status', title: 'Control Status Report', description: 'Detailed status of all 51 SOC 2 controls.', icon: Shield, color: '#7c3aed' },
    { id: 'policy_summary', title: 'Policy Library Summary', description: 'Overview of all compliance policies and their owners.', icon: FileText, color: '#d97706' },
    { id: 'findings_report', title: 'Security Findings Report', description: 'Active security findings and remediation status.', icon: AlertTriangle, color: '#dc2626' },
    { id: 'pentest_summary', title: 'Pen Test Summary', description: 'Penetration testing results and remediation status.', icon: Bug, color: '#7c3aed' },
    { id: 'accessibility_report', title: 'Accessibility Report', description: 'WCAG compliance audit results and element scores.', icon: Eye, color: '#0891b2' },
    { id: 'caiq_report', title: 'CAIQ v4.1.0 Response', description: 'Cloud Security Alliance questionnaire responses.', icon: Globe, color: '#6b7280' },
  ];

  return (
    <div className="reports-page">
      <div className="page-header">
        <div>
          <h1>Compliance Reports</h1>
          <p>Generate branded compliance reports for auditors and clients</p>
        </div>
      </div>

      {/* Generate New Reports */}
      <h2 className="section-header">Generate Reports</h2>
      <div className="reports-grid">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isGenerating = generating === report.id;
          return (
            <Card key={report.id} className="report-type-card">
              <div className="report-icon" style={{ background: report.color + '15', color: report.color }}>
                <Icon size={24} />
              </div>
              <h3>{report.title}</h3>
              <p>{report.description}</p>
              <button
                onClick={() => handleGenerateReport(report.id)}
                disabled={generating !== null}
                className="btn-primary"
                data-testid={`generate-${report.id}`}
              >
                {isGenerating ? <Loader2 className="spin" size={16} /> : <Download size={16} />}
                {isGenerating ? 'Generating...' : 'Generate PDF'}
              </button>
            </Card>
          );
        })}
      </div>

      {/* Generated Reports History */}
      <h2 className="section-header" style={{ marginTop: 32 }}>Generated Reports</h2>
      {loadingReports ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : reports.length > 0 ? (
        <div className="reports-history">
          {reports.map((report) => (
            <Card key={report.report_id} className="report-history-card">
              <div className="report-info">
                <h4>{report.title}</h4>
                <div className="report-meta">
                  <Badge color="#6b7280">{report.report_type?.replace(/_/g, ' ')}</Badge>
                  <span>Generated {new Date(report.generated_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="report-actions">
                <button
                  onClick={() => handleDownloadReport(report.report_id)}
                  className="link-btn"
                  data-testid={`download-${report.report_id}`}
                >
                  <Download size={16} /> PDF
                </button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="empty-state">
          <FileText size={48} color="#6b7280" />
          <h3>No reports generated yet</h3>
          <p>Generate your first compliance report above.</p>
        </Card>
      )}

      <Card className="audit-export">
        <h3>Audit Evidence Package</h3>
        <p>Generate a comprehensive audit-ready evidence package with all controls, evidence, and findings.</p>
        <button className="btn-success" data-testid="generate-audit-package" onClick={() => toast.info('Audit package generation coming soon')}>
          <Package size={16} /> Generate Audit Package
        </button>
      </Card>
    </div>
  );
}
